<template>
  <iColumn
    v-if="previewsBothStates"
    gap="extraSmall"
    width="fill"
    height="hug"
    data-test="option-state-preview"
  >
    <AppComponentOverrideHost
      v-for="componentId in previewComponentIds"
      :key="componentId"
      :app-name="questionnaireAppName"
      :dynamic-component-id="componentId"
      :contexts="previewContexts"
      :theme-mode="themeMode"
    />
  </iColumn>
  <AppComponentOverrideHost
    v-else-if="resolvedComponentId && resolvedContexts"
    :app-name="questionnaireAppName"
    :dynamic-component-id="resolvedComponentId"
    :contexts="resolvedContexts"
    :theme-mode="themeMode"
  />
  <iText
    v-else-if="editMode"
    variant="subtle"
    font-size="small"
    data-test="option-state-empty"
  >
    {{ emptyStateMessage }}
  </iText>
</template>

<script>
import App from "@bloglovin/inspire-engine/App";
import AppComponentOverrideHost
  from "%/dynamic-component-editor/standard-elements/dynamic-app-elements/display/AppComponentOverrideHost.vue";
import { DynamicElementMixin } from "%/dynamic-component-editor/mixins/dynamic-element-mixin";
import { resolveBoundEntityId, resolveNamedContextEntityId }
  from "%/dynamic-component-editor/helpers/bound-context-entity-id";
import { declaredAppOption, declaredAppOptions } from "%/dynamic-component-editor/options/declared-app-options";
import AppearanceOptions from "%/dynamic-component-editor/bundles/appearance-options";
import { OPTION_STATES_OPTION_KEY } from "+/QuizzesPolls/Components/questionnaire-card-selection";
import { QUIZZES_POLLS_APP_NAME } from "+/QuizzesPolls/app-name";

const SELECTED_CARD_KEY = "option-card-selected";
const DESELECTED_CARD_KEY = "option-card-deselected";

const OPTION_CONTEXT_NAME = "option";
const ANSWER_CONTEXT_NAME = "answer";
const OPTION_ENTITY_TYPE = "QuestionOption";
const ANSWER_ENTITY_TYPE = "Answer";
const ANSWER_OPTION_ENTITY_TYPE = "AnswerOption";
const QUESTION_ENTITY_TYPE = "Question";
const QUESTION_FIELD = "question";
const UX_ELEMENT_FIELD = "uxElement";
const SELECTED_OPTIONS_FIELD = "selectedOptions";
const MULTIPLE_CHOICE_UX = "multiple_choice";
const STATUS_ACTIVE = "active";
const DEFAULT_APP_NAME = "QuizzesPolls";

const MSG_NO_CONTEXT = "Drop this inside an option row, so it has an option and an answer to read.";
const MSG_NO_SELECTED = "No component set for the chosen state.";
const MSG_NO_DESELECTED = "No component set for the not-chosen state.";

export default {
  name: "OptionStateElement",
  components: { AppComponentOverrideHost },
  mixins: [DynamicElementMixin],
  inject: {
    pageEntities: { default: null },
    scopedProvidedContexts: { default: null },
  },
  elementPanelSettings: {
    displayName: "Option State",
    displayIcon: "mdi:checkbox-marked-circle-outline",
    elementType: "element",
    elementGroup: "general",
  },
  options: [
    ...declaredAppOptions(QUIZZES_POLLS_APP_NAME, [
      OPTION_STATES_OPTION_KEY,
      SELECTED_CARD_KEY,
      DESELECTED_CARD_KEY,
    ]),
  ],
  optionBundles: [new AppearanceOptions()],
  computed: {
    questionnaireAppName() {
      return this.elementContext?.appName || DEFAULT_APP_NAME;
    },
    questionnaireApp() {
      try {
        return App.get(this.questionnaireAppName);
      } catch {
        return null;
      }
    },
    elementContext() {
      return this.optionValues?.context || this.configuration?.options?.context || {};
    },
    optionEntityId() {
      return resolveBoundEntityId(this, OPTION_CONTEXT_NAME) || 0;
    },
    // Resolved BY NAME, not through the element's own binding: this element
    // reads two contexts, and options.context names only the first. Routing
    // this one through the binding too returned the QuestionOption id as the
    // Answer id for any instance carrying a stamped context -- which every
    // palette drop does, while the shipped elements carry none.
    answerEntityId() {
      return resolveNamedContextEntityId(this, ANSWER_CONTEXT_NAME) || 0;
    },
    questionEntityId() {
      const question = this.entityField(ANSWER_ENTITY_TYPE, this.answerEntityId, QUESTION_FIELD);
      return Number(question?.pk?.id ?? question?.id ?? 0);
    },
    isMultiSelect() {
      return this.entityField(QUESTION_ENTITY_TYPE, this.questionEntityId, UX_ELEMENT_FIELD)
        === MULTIPLE_CHOICE_UX;
    },
    selectedOptionValues() {
      if (!this.answerEntityId || !this.questionnaireApp) {
        return [];
      }
      const items = this.questionnaireApp.getFeedItems(
        ANSWER_ENTITY_TYPE,
        { id: this.answerEntityId },
        SELECTED_OPTIONS_FIELD,
      ) ?? [];
      return items
          .map(item => item?.entityPk ?? item?.pk ?? null)
          .filter(Boolean)
          .filter(entityPk => this.entityField(ANSWER_OPTION_ENTITY_TYPE, entityPk.id, "status")
            === STATUS_ACTIVE)
          .map(entityPk => this.entityField(ANSWER_OPTION_ENTITY_TYPE, entityPk.id, "value"))
          .filter(value => value !== null && value !== undefined && value !== "");
    },
    isChosen() {
      const optionValue = this.entityField(OPTION_ENTITY_TYPE, this.optionEntityId, "value");
      if (optionValue === null || optionValue === undefined || optionValue === "") {
        return false;
      }
      if (this.isMultiSelect) {
        return this.selectedOptionValues.includes(optionValue);
      }
      return optionValue === this.entityField(ANSWER_ENTITY_TYPE, this.answerEntityId, "value");
    },
    resolvedComponentId() {
      return this.componentIdForKey(this.isChosen ? SELECTED_CARD_KEY : DESELECTED_CARD_KEY);
    },
    resolvedContexts() {
      if (!this.optionEntityId || !this.answerEntityId) {
        return null;
      }
      return {
        [OPTION_CONTEXT_NAME]: {
          entityType: OPTION_ENTITY_TYPE,
          pk: { id: this.optionEntityId },
        },
        [ANSWER_CONTEXT_NAME]: {
          entityType: ANSWER_ENTITY_TYPE,
          pk: { id: this.answerEntityId },
        },
      };
    },
    previewsBothStates() {
      return Boolean(this.editMode) && !this.resolvedContexts && this.previewComponentIds.length > 0;
    },
    previewComponentIds() {
      return [SELECTED_CARD_KEY, DESELECTED_CARD_KEY]
          .map(key => this.componentIdForKey(key))
          .filter(componentId => componentId > 0);
    },
    previewContexts() {
      return {
        [OPTION_CONTEXT_NAME]: { entityType: OPTION_ENTITY_TYPE, pk: { id: 0 } },
        [ANSWER_CONTEXT_NAME]: { entityType: ANSWER_ENTITY_TYPE, pk: { id: 0 } },
      };
    },
    emptyStateMessage() {
      if (!this.resolvedContexts) {
        return MSG_NO_CONTEXT;
      }
      return this.isChosen ? MSG_NO_SELECTED : MSG_NO_DESELECTED;
    },
  },
  methods: {
    componentIdForKey(key) {
      const optionClass = declaredAppOption(QUIZZES_POLLS_APP_NAME, key);
      if (!optionClass) {
        return 0;
      }
      return Number(this.getOptionValue(optionClass)) || 0;
    },
    entityField(entityType, entityId, fieldName) {
      if (!entityId || !this.questionnaireApp) {
        return null;
      }
      try {
        return this.questionnaireApp.getEntityField(entityType, { id: entityId }, fieldName);
      } catch {
        return null;
      }
    },
  },
};
</script>
